import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Settings as SettingsIcon, 
  Mail, 
  DollarSign, 
  Sparkles,
  Zap,
  Smartphone,
  Database,
  Wifi,
  WifiOff,
  Globe,
  CheckCircle,
  XCircle,
  Palette,
  Check,
  Sun,
  Moon,
  FileText,
  Key,
  Brain,
  Bell,
  Info,
  ChevronRight,
  ArrowLeft,
  Shield,
  HardDrive,
  Server,
  Plus,
  Trash2,
  RefreshCw,
  Lock,
  Unlock,
  Save,
  X,
  LayoutGrid,
  Contact,
  LayoutDashboard,
  Instagram,
  Video,
  PanelLeft,
  PanelLeftClose,
  BarChart3,
  Activity,
  MessageCircle,
  Layers,
  Heart
} from 'lucide-react';
import { useTheme, generateThemeFromPalette } from '../contexts/ThemeContext';
import SharedPageHeader from '../components/layout/PageHeader';
import { wsRemoteUrl } from '../config/api';

const PageContainer = styled.div`
  padding: 20px;
`;

const Section = styled.div`
  background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg) 100%);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: var(--shadow);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, var(--primary), transparent); opacity: 0.3;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--border);
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 700;
  color: var(--text-secondary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SectionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: var(--shadow);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 8px;
    padding: 1px;
    background: linear-gradient(135deg, rgba(255,255,255,0.1), transparent);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
`;

const ToggleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ToggleItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${props => props.$enabled ? 'rgba(88, 101, 242, 0.15)' : 'var(--bg)'};
  border-radius: 8px;
  border: 1px solid ${props => props.$enabled ? 'var(--primary)' : 'var(--card-border)'};
  transition: all 0.2s ease;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    border-color: ${props => props.$disabled ? 'var(--card-border)' : 'var(--primary)'};
    background: ${props => props.$disabled ? 'var(--bg)' : props.$enabled ? 'rgba(88, 101, 242, 0.2)' : 'var(--card-bg)'};
    transform: ${props => props.$disabled ? 'none' : 'translateY(-1px)'};
  }
`;

const ToggleLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ToggleIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.$enabled ? 'var(--primary)' : 'var(--card-border)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$enabled ? 'white' : 'var(--text-secondary)'};
  transition: all 0.2s ease;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$enabled ? 'var(--btn-text)' : 'var(--text-secondary)'};
  transition: color 0.2s ease;
`;

const ToggleSwitch = styled.button`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: ${props => props.$enabled ? 'var(--primary)' : 'var(--card-border)'};
  border: none;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  position: relative;
  transition: background 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$enabled ? '22px' : '2px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.15);
  }

  &:hover {
    background: ${props => props.$disabled ? (props.$enabled ? 'var(--primary)' : 'var(--card-border)') : (props.$enabled ? 'var(--secondary)' : 'var(--surface-hover)')};
  }
`;

const DisabledBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: var(--warning);
  background: rgba(250, 166, 26, 0.15);
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
  text-transform: uppercase;
`;

const ThemeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;
`;

const ThemeCard = styled.div`
  position: relative;
  border-radius: 12px;
  border: 2px solid ${props => props.$active ? props.$themeColor : 'var(--card-border)'};
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-hover);
    border-color: ${props => props.$themeColor};
  }
`;

const ThemePreview = styled.div`
  height: 52px;
  background: ${props => props.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px;
`;

const ThemePreviewSidebar = styled.div`
  width: 16px;
  height: 38px;
  border-radius: 3px;
  background: ${props => props.$color};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 4px;
  gap: 3px;
`;

const ThemePreviewDot = styled.div`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${props => props.$color || 'rgba(255,255,255,0.3)'};
`;

const ThemePreviewContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ThemePreviewBar = styled.div`
  height: 5px;
  border-radius: 3px;
  background: ${props => props.$color};
  width: ${props => props.$width || '100%'};
`;

const ThemePreviewCard = styled.div`
  height: 14px;
  border-radius: 3px;
  background: ${props => props.$color};
  border: 1px solid ${props => props.$border};
`;

const ThemeInfo = styled.div`
  padding: 6px 8px;
  background: var(--card-bg);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ThemeName = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ThemeMode = styled.div`
  font-size: 11px;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ThemeActiveCheck = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
`;

const RemoteSection = styled.div`
  margin-top: 16px;
`;

const RemoteConfig = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: end;
`;

const RemoteInput = styled.div`
  label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
    margin-bottom: 8px;
    text-transform: uppercase;
  }
  input {
    width: 100%;
    padding: 12px 16px;
    background: var(--surface);
    border: 1px solid var(--card-border);
    border-radius: 8px;
    color: var(--text-primary);
    font-size: 14px;
    font-family: monospace;
    
    &:focus {
      outline: none;
      border-color: var(--warning);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const RemoteButton = styled.button`
  padding: 12px 24px;
  background: ${props => props.$connected ? 'var(--error)' : 'var(--success)'};
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.$connected ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// ── Card Navigation (taktik-bot style) ──────────────────────────────

const NavGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const NavCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px;
  background: ${p => p.$color ? `linear-gradient(135deg, ${p.$color}18, ${p.$color}08)` : 'var(--card-bg)'};
  border: 1px solid ${p => p.$color ? `${p.$color}30` : 'var(--card-border)'};
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-hover);
    border-color: ${p => p.$color || 'var(--primary)'};
  }
`;

const NavCardIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${p => p.$color || 'var(--primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const NavCardText = styled.div`
  flex: 1;
  min-width: 0;
  h3 { font-size: 15px; font-weight: 700; color: var(--text-primary); margin: 0 0 2px; }
  p { font-size: 12px; color: var(--text-muted); margin: 0; }
`;

const NavCardArrow = styled(ChevronRight)`
  color: var(--text-muted);
  flex-shrink: 0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 16px;
  transition: all 0.15s;
  &:hover { background: var(--surface-hover); color: var(--text-primary); }
`;

const PanelTitle = styled.h2`
  font-size: 20px;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DbToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 10px;
  margin-bottom: 10px;
`;

const DbToggleLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  span { font-size: 14px; font-weight: 600; color: var(--text-primary); }
  small { font-size: 11px; color: var(--text-muted); display: block; margin-top: 2px; }
`;

const DbStatusBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  background: ${p => p.$active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)'};
  color: ${p => p.$active ? 'var(--success)' : 'var(--text-muted)'};
`;

const LicencePlanCard = styled.div`
  padding: 24px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  border-radius: 14px;
  color: white;
  margin-bottom: 16px;
  h3 { font-size: 18px; font-weight: 800; margin: 0 0 4px; }
  p { font-size: 13px; opacity: 0.85; margin: 0; }
`;

const LicenceFeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 16px;
`;

const LicenceFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$active ? 'var(--success)' : 'var(--text-muted)'};
`;

const Settings = () => {
  const { currentTheme, setCurrentTheme, themes, addCustomTheme, removeCustomTheme } = useTheme();
  const [activePanel, setActivePanel] = useState(null);



  // DB mode (server-side) — 'sqlite' | 'postgres' | 'both'
  const [dbMode, setDbModeState] = useState('both');
  const [dbModeSaving, setDbModeSaving] = useState(false);
  const [dbModeChanged, setDbModeChanged] = useState(false);

  useEffect(() => {
    fetch('/api/settings/db-mode')
      .then(r => r.json())
      .then(d => { if (d.mode) setDbModeState(d.mode); })
      .catch(() => {});
  }, []);

  const changeDbMode = async (mode) => {
    setDbModeSaving(true);
    try {
      const r = await fetch('/api/settings/db-mode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode })
      });
      const d = await r.json();
      if (d.success) { setDbModeState(d.mode); setDbModeChanged(true); }
    } catch (_) {}
    setDbModeSaving(false);
  };

  // Licence info
  const [licenceInfo] = useState({
    plan: 'Pro',
    maxDevices: 10,
    features: ['interact', 'scrape', 'unfollow', 'dm', 'warmup', 'filtering', 'scheduling', 'enrichment']
  });

  // ── Sidebar config ──
  const SIDEBAR_CONFIG_KEY = 'qlickr_sidebar_config';
  const DEFAULT_SIDEBAR_CONFIG = {
    iconSidebar: { devices: true, crm: true, 'impulse-bot': true, 'accounts-list': false, 'workflow-builder': false, 'script-flows': false, gmail: false, tiktok: false, google: false, twitter: false, tinder: false, data: false, 'device-info': false },
    textSidebar: {
      sections: { 'device-info': true, insta: true, gmail: true, tiktok: true, google: true, twitter: true, tinder: true, data: true, devices: false, crm: false, 'impulse-bot': false, 'workflow-builder': false, 'script-flows': false },
      bottomNav: { 'accounts-list': true, 'impulse-bot': true, 'workflow-builder': true, devices: true, crm: false, gmail: false, tiktok: false, google: false, twitter: false, tinder: false, data: false, 'device-info': false, 'script-flows': false, settings: false },
    },
  };
  const [sidebarConfig, setSidebarConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_CONFIG_KEY);
      if (saved) {
        const p = JSON.parse(saved);
        return {
          iconSidebar: { ...DEFAULT_SIDEBAR_CONFIG.iconSidebar, ...p.iconSidebar },
          textSidebar: {
            sections: { ...DEFAULT_SIDEBAR_CONFIG.textSidebar.sections, ...p.textSidebar?.sections },
            bottomNav: { ...DEFAULT_SIDEBAR_CONFIG.textSidebar.bottomNav, ...p.textSidebar?.bottomNav },
          },
        };
      }
    } catch (_) {}
    return { ...DEFAULT_SIDEBAR_CONFIG };
  });

  const updateSidebarConfig = (newConfig) => {
    setSidebarConfig(newConfig);
    localStorage.setItem(SIDEBAR_CONFIG_KEY, JSON.stringify(newConfig));
    window.dispatchEvent(new Event('sidebarConfigChanged'));
  };

  const toggleIconSidebar = (key) => {
    const updated = { ...sidebarConfig, iconSidebar: { ...sidebarConfig.iconSidebar, [key]: !sidebarConfig.iconSidebar[key] } };
    updateSidebarConfig(updated);
  };

  const toggleTextSection = (key) => {
    const updated = { ...sidebarConfig, textSidebar: { ...sidebarConfig.textSidebar, sections: { ...sidebarConfig.textSidebar.sections, [key]: !sidebarConfig.textSidebar.sections[key] } } };
    updateSidebarConfig(updated);
  };

  const toggleBottomNav = (key) => {
    const updated = { ...sidebarConfig, textSidebar: { ...sidebarConfig.textSidebar, bottomNav: { ...sidebarConfig.textSidebar.bottomNav, [key]: !sidebarConfig.textSidebar.bottomNav[key] } } };
    updateSidebarConfig(updated);
  };

  // ── Theme Builder State ──
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderName, setBuilderName] = useState('');
  const [builderColors, setBuilderColors] = useState({
    primary: '#8B5CF6', secondary: '#A855F7', accent: '#C084FC',
    background: '#1e1f22', surface: '#2b2d31'
  });
  const [builderPreview, setBuilderPreview] = useState(null);
  const [lockedColors, setLockedColors] = useState({});

  const hslToHex = (h, s, l) => {
    h = ((h%360)+360)%360; s = Math.max(0, Math.min(100, s))/100; l = Math.max(0, Math.min(100, l))/100;
    const a = s * Math.min(l, 1-l);
    const f = (n) => { const k = (n + h/30) % 12; return Math.round(255 * (l - a * Math.max(-1, Math.min(k-3, 9-k, 1)))); };
    return '#' + [f(0),f(8),f(4)].map(v => v.toString(16).padStart(2,'0')).join('');
  };

  const generateHarmoniousPalette = () => {
    // Pick a random base hue, then use color harmony rules
    const schemes = ['analogous', 'triadic', 'splitComp', 'tetradic'];
    const scheme = schemes[Math.floor(Math.random() * schemes.length)];
    const baseH = Math.random() * 360;
    const baseSat = 55 + Math.random() * 30; // 55-85%
    const isDark = Math.random() > 0.3; // 70% chance dark

    let h1, h2, h3;
    if (scheme === 'analogous') { h1 = baseH; h2 = baseH + 30; h3 = baseH - 30; }
    else if (scheme === 'triadic') { h1 = baseH; h2 = baseH + 120; h3 = baseH + 240; }
    else if (scheme === 'splitComp') { h1 = baseH; h2 = baseH + 150; h3 = baseH + 210; }
    else { h1 = baseH; h2 = baseH + 90; h3 = baseH + 180; }

    const newColors = {};
    if (!lockedColors.primary) newColors.primary = hslToHex(h1, baseSat, isDark ? 65 : 50);
    if (!lockedColors.secondary) newColors.secondary = hslToHex(h2, baseSat - 5, isDark ? 60 : 45);
    if (!lockedColors.accent) newColors.accent = hslToHex(h3, baseSat + 10, isDark ? 70 : 55);
    if (!lockedColors.background) newColors.background = isDark ? hslToHex(h1, 10, 10 + Math.random()*5) : hslToHex(h1, 10, 95 + Math.random()*4);
    if (!lockedColors.surface) newColors.surface = isDark ? hslToHex(h1, 10, 15 + Math.random()*5) : hslToHex(h1, 8, 90 + Math.random()*4);

    setBuilderColors(prev => ({ ...prev, ...newColors }));
  };

  const toggleLock = (colorKey) => {
    setLockedColors(prev => ({ ...prev, [colorKey]: !prev[colorKey] }));
  };

  const handleHexInput = (colorKey, val) => {
    const cleaned = val.startsWith('#') ? val : '#' + val;
    if (/^#[0-9A-Fa-f]{0,6}$/.test(cleaned)) {
      setBuilderColors(prev => ({ ...prev, [colorKey]: cleaned }));
    }
  };

  useEffect(() => {
    if (builderOpen && builderColors.primary && builderColors.primary.length === 7) {
      setBuilderPreview(generateThemeFromPalette(builderName || 'Preview', builderColors));
    }
  }, [builderColors, builderName, builderOpen]);

  const saveCustomTheme = () => {
    if (!builderName.trim()) return;
    const key = 'custom_' + builderName.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
    const themeObj = generateThemeFromPalette(builderName.trim(), builderColors);
    addCustomTheme(key, themeObj);
    setCurrentTheme(key);
    setBuilderOpen(false);
    setBuilderName('');
  };

  // ── API Keys State ──
  const [apiKeys, setApiKeys] = useState({});
  const [apiKeyInputs, setApiKeyInputs] = useState({});
  const [apiKeySaving, setApiKeySaving] = useState({});
  const [apiKeyStatus, setApiKeyStatus] = useState({});

  // ── Gemini Model Selector State ──
  const [, setGeminiModels] = useState([]);
  const [, setGeminiCurrentModel] = useState('');
  const [, setGeminiSelectedModel] = useState('');

  // ── Per-service AI model state ──
  const [aiServices, setAiServices] = useState([]);
  const [aiServiceSelections, setAiServiceSelections] = useState({}); // { serviceId: selectedModel }
  const [aiServiceSaving, setAiServiceSaving] = useState({}); // { serviceId: bool }
  const [aiServiceStatus, setAiServiceStatus] = useState({}); // { serviceId: 'saved' | 'error' | null }

  // ── AI Usage tracking state ──
  const [aiUsage, setAiUsage] = useState(null);
  const [aiUsagePeriod, setAiUsagePeriod] = useState('week');
  const [aiUsageLoading, setAiUsageLoading] = useState(false);

  const loadAiUsage = async (period) => {
    setAiUsageLoading(true);
    try {
      const r = await fetch(`/api/ai-usage?period=${period || aiUsagePeriod}`);
      if (r.ok) {
        const data = await r.json();
        setAiUsage(data);
      }
    } catch (_) {}
    setAiUsageLoading(false);
  };

  // Load AI usage when AI panel opens
  useEffect(() => {
    if (activePanel === 'ai' && !aiUsage) loadAiUsage(aiUsagePeriod);
    if (activePanel === 'ai' && aiServices.length === 0) loadAiServices();
  }, [activePanel]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGeminiModel = async () => {
    try {
      const r = await fetch('/api/gemini/model');
      if (r.ok) {
        const data = await r.json();
        setGeminiModels(data.models || []);
        setGeminiCurrentModel(data.model || '');
        setGeminiSelectedModel(data.model || '');
      }
    } catch (_) {}
  };

  // ── Per-service AI model functions ──
  const loadAiServices = async () => {
    try {
      const r = await fetch('/api/gemini/ai-services');
      if (r.ok) {
        const data = await r.json();
        setAiServices(data.services || []);
        const selections = {};
        (data.services || []).forEach(s => { selections[s.id] = s.currentModel; });
        setAiServiceSelections(selections);
      }
    } catch (_) {}
  };

  const saveAiServiceModel = async (serviceId) => {
    const svc = aiServices.find(s => s.id === serviceId);
    const selected = aiServiceSelections[serviceId];
    if (!selected || selected === svc?.currentModel) return;
    setAiServiceSaving(p => ({ ...p, [serviceId]: true }));
    try {
      const r = await fetch(`/api/gemini/ai-services/${serviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: selected })
      });
      if (r.ok) {
        setAiServiceStatus(p => ({ ...p, [serviceId]: 'saved' }));
        // Update the service's currentModel in the local state
        setAiServices(prev => prev.map(s => s.id === serviceId ? { ...s, currentModel: selected } : s));
        setTimeout(() => setAiServiceStatus(p => ({ ...p, [serviceId]: null })), 3000);
      } else {
        setAiServiceStatus(p => ({ ...p, [serviceId]: 'error' }));
      }
    } catch (_) {
      setAiServiceStatus(p => ({ ...p, [serviceId]: 'error' }));
    }
    setAiServiceSaving(p => ({ ...p, [serviceId]: false }));
  };

  const API_SERVICES = [
    { id: 'gemini', name: 'Google Gemini', icon: Sparkles, color: '#8b5cf6', placeholder: 'AIza...', desc: 'Analyse, enrichissement, chat IA' },
    { id: 'openai', name: 'OpenAI / ChatGPT', icon: Brain, color: '#10a37f', placeholder: 'sk-...', desc: 'GPT-4, GPT-3.5' },
    { id: 'claude', name: 'Anthropic Claude', icon: Zap, color: '#d97706', placeholder: 'sk-ant-...', desc: 'Analyse avancée' },
    { id: 'notion', name: 'Notion', icon: FileText, color: '#000000', placeholder: 'secret_...', desc: 'Sync CRM → Notion' },
    { id: 'google_sheets', name: 'Google Sheets', icon: Database, color: '#0f9d58', placeholder: 'Clé API', desc: 'Export de stats' },
  ];

  const GOOGLE_AUTH_FIELDS = [
    { id: 'google_client_id', label: 'Google OAuth Client ID', placeholder: '123456-xxxxx.apps.googleusercontent.com', icon: Globe },
    { id: 'google_client_secret', label: 'Google OAuth Client Secret', placeholder: 'GOCSPX-...', icon: Lock },
  ];

  const loadApiKeys = async () => {
    try {
      const r = await fetch('/api/credentials');
      if (r.ok) {
        const data = await r.json();
        setApiKeys(data);
        const inputs = {};
        [...API_SERVICES, ...GOOGLE_AUTH_FIELDS].forEach(s => { inputs[s.id] = ''; });
        setApiKeyInputs(inputs);
      }
    } catch (_) {}
  };

  const saveApiKey = async (service) => {
    const key = apiKeyInputs[service];
    if (!key?.trim()) return;
    setApiKeySaving(p => ({ ...p, [service]: true }));
    try {
      const r = await fetch(`/api/credentials/${service}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: key.trim() })
      });
      if (r.ok) {
        setApiKeyStatus(p => ({ ...p, [service]: 'saved' }));
        setApiKeyInputs(p => ({ ...p, [service]: '' }));
        await loadApiKeys();
        setTimeout(() => setApiKeyStatus(p => ({ ...p, [service]: null })), 3000);
      }
    } catch (_) {
      setApiKeyStatus(p => ({ ...p, [service]: 'error' }));
    }
    setApiKeySaving(p => ({ ...p, [service]: false }));
  };

  const deleteApiKey = async (service) => {
    try {
      await fetch(`/api/credentials/${service}`, { method: 'DELETE' });
      await loadApiKeys();
    } catch (_) {}
  };

  const [, setBusinessStats] = useState({ emailsCreated: 0, emailMarketValue: 0, activeClients: 0, totalRevenue: 0 });

  // WebSocket Remote State
  const [, setRemoteEnabled] = useState(false);
  const [remoteConnected, setRemoteConnected] = useState(false);
  const [remoteHost, setRemoteHost] = useState('');
  const [wsConnection, setWsConnection] = useState(null);
  const [screenStreamingEnabled, setScreenStreamingEnabled] = useState(true);

  useEffect(() => {
    const savedRemoteHost = localStorage.getItem('remoteHost');
    if (savedRemoteHost) setRemoteHost(savedRemoteHost);
    const savedRemoteEnabled = localStorage.getItem('remoteEnabled') === 'true';
    if (savedRemoteEnabled && savedRemoteHost) connectToRemote(savedRemoteHost);
    const savedScreenStreaming = localStorage.getItem('screenStreamingEnabled');
    if (savedScreenStreaming !== null) setScreenStreamingEnabled(savedScreenStreaming === 'true');
    fetchBusinessStats();
    loadApiKeys();
    loadGeminiModel();
    return () => { if (wsConnection) wsConnection.close(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBusinessStats = async () => {
    try {
      const response = await fetch('/api/business-stats');
      if (response.ok) {
        const data = await response.json();
        setBusinessStats(data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des stats business:', error);
    }
  };

  const connectToRemote = (host) => {
    if (!host) return;
    
    try {
      const ws = new WebSocket(wsRemoteUrl(host));
      
      ws.onopen = () => {
        console.log('🔌 Connected to remote server');
        setRemoteConnected(true);
        setRemoteEnabled(true);
        localStorage.setItem('remoteEnabled', 'true');
        localStorage.setItem('remoteHost', host);
        
        // Stocker la connexion globalement pour les autres composants
        window.remoteWebSocket = ws;
        window.dispatchEvent(new Event('remoteConnectionChanged'));
      };
      
      ws.onclose = () => {
        console.log('🔌 Disconnected from remote server');
        setRemoteConnected(false);
        setRemoteEnabled(false);
        localStorage.setItem('remoteEnabled', 'false');
        window.remoteWebSocket = null;
        window.dispatchEvent(new Event('remoteConnectionChanged'));
      };
      
      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setRemoteConnected(false);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 Remote message:', data);
        } catch (e) {
          console.error('Error parsing message:', e);
        }
      };
      
      setWsConnection(ws);
    } catch (error) {
      console.error('❌ Failed to connect:', error);
      setRemoteConnected(false);
    }
  };

  const disconnectFromRemote = () => {
    if (wsConnection) {
      wsConnection.close();
      setWsConnection(null);
    }
    setRemoteConnected(false);
    setRemoteEnabled(false);
    localStorage.setItem('remoteEnabled', 'false');
    window.remoteWebSocket = null;
    window.dispatchEvent(new Event('remoteConnectionChanged'));
  };

  const handleRemoteToggle = () => {
    if (remoteConnected) {
      disconnectFromRemote();
    } else {
      if (remoteHost) {
        connectToRemote(remoteHost);
      }
    }
  };

  const handleScreenStreamingToggle = async () => {
    const newValue = !screenStreamingEnabled;
    setScreenStreamingEnabled(newValue);
    localStorage.setItem('screenStreamingEnabled', newValue.toString());
    
    // Envoyer au backend
    try {
      await fetch('/api/settings/screen-streaming', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newValue })
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du paramètre WebSocket:', error);
    }
  };

  // ── Sub-panel renderers ──────────────────────────────────────────
  const renderAppearance = () => {
    const ICON_SIDEBAR_ITEMS = [
      { key: 'devices', label: 'Devices', icon: LayoutGrid, color: '#6366f1' },
      { key: 'crm', label: 'CRM', icon: Contact, color: '#3b82f6' },
      { key: 'impulse-bot', label: 'Centre de Contrôle', icon: LayoutDashboard, color: '#22c55e' },
      { key: 'accounts-list', label: 'Instagram', icon: Instagram, color: '#e1306c' },
      { key: 'workflow-builder', label: 'Workflows', icon: Zap, color: '#f59e0b' },
      { key: 'script-flows', label: 'Script Flows', icon: Layers, color: '#a591ef' },
      { key: 'gmail', label: 'Gmail', icon: Mail, color: '#ea4335' },
      { key: 'tiktok', label: 'TikTok', icon: Video, color: '#000000' },
      { key: 'google', label: 'Google', icon: Globe, color: '#4285f4' },
      { key: 'twitter', label: 'Twitter/X', icon: MessageCircle, color: '#000000' },
      { key: 'tinder', label: 'Tinder', icon: Heart, color: '#FE3C72' },
      { key: 'data', label: 'Données', icon: Database, color: '#06b6d4' },
      { key: 'device-info', label: 'Informations', icon: Info, color: '#6b7280' },
      { key: 'settings', label: 'Paramètres', icon: SettingsIcon, color: '#6b7280', locked: true },
    ];
    const TEXT_SECTION_ITEMS = [
      { key: 'device-info', label: 'Informations', icon: Info, color: '#6b7280' },
      { key: 'insta', label: 'Instagram', icon: Instagram, color: '#e1306c' },
      { key: 'gmail', label: 'Gmail', icon: Mail, color: '#ea4335' },
      { key: 'tiktok', label: 'TikTok', icon: Video, color: '#000000' },
      { key: 'google', label: 'Google', icon: Globe, color: '#4285f4' },
      { key: 'twitter', label: 'Twitter/X', icon: MessageCircle, color: '#000000' },
      { key: 'tinder', label: 'Tinder', icon: Heart, color: '#FE3C72' },
      { key: 'data', label: 'Données', icon: Database, color: '#06b6d4' },
      { key: 'devices', label: 'Devices', icon: LayoutGrid, color: '#6366f1' },
      { key: 'crm', label: 'CRM', icon: Contact, color: '#3b82f6' },
      { key: 'impulse-bot', label: 'Centre de Contrôle', icon: LayoutDashboard, color: '#22c55e' },
      { key: 'workflow-builder', label: 'Workflows', icon: Zap, color: '#f59e0b' },
      { key: 'script-flows', label: 'Script Flows', icon: Layers, color: '#a591ef' },
    ];
    const BOTTOM_NAV_ITEMS = [
      { key: 'accounts-list', label: 'Instagram', icon: Instagram, color: '#e1306c' },
      { key: 'impulse-bot', label: 'Centre de contrôle', icon: LayoutDashboard, color: '#22c55e' },
      { key: 'workflow-builder', label: 'Workflows', icon: Zap, color: '#f59e0b' },
      { key: 'devices', label: 'Devices', icon: Smartphone, color: '#6366f1' },
      { key: 'crm', label: 'CRM', icon: Contact, color: '#3b82f6' },
      { key: 'gmail', label: 'Gmail', icon: Mail, color: '#ea4335' },
      { key: 'tiktok', label: 'TikTok', icon: Video, color: '#000000' },
      { key: 'google', label: 'Google', icon: Globe, color: '#4285f4' },
      { key: 'twitter', label: 'Twitter/X', icon: MessageCircle, color: '#000000' },
      { key: 'tinder', label: 'Tinder', icon: Heart, color: '#FE3C72' },
      { key: 'data', label: 'Données', icon: Database, color: '#06b6d4' },
      { key: 'device-info', label: 'Informations', icon: Info, color: '#6b7280' },
      { key: 'script-flows', label: 'Script Flows', icon: Layers, color: '#a591ef' },
      { key: 'settings', label: 'Paramètres', icon: SettingsIcon, color: '#6b7280' },
    ];
    const renderToggleRow = (item, enabled, onToggle, locked = false) => (
      <ToggleItem key={item.key} $enabled={enabled} $disabled={locked} onClick={() => !locked && onToggle(item.key)}>
        <ToggleLabelContainer>
          <ToggleIconWrapper $enabled={enabled}>
            <item.icon size={16} />
          </ToggleIconWrapper>
          <div>
            <ToggleLabel $enabled={enabled}>{item.label}</ToggleLabel>
            {locked && <DisabledBadge>Verrouillé</DisabledBadge>}
          </div>
        </ToggleLabelContainer>
        <ToggleSwitch $enabled={enabled} $disabled={locked} onClick={e => { e.stopPropagation(); if (!locked) onToggle(item.key); }} />
      </ToggleItem>
    );
    return (
    <>
      <BackButton onClick={() => setActivePanel(null)}><ArrowLeft size={16} /> Retour</BackButton>
      <PanelTitle><Palette size={22} color="var(--primary)" /> Apparence</PanelTitle>

      {/* Theme Grid */}
      <Section>
        <SectionHeader>
          <SectionIcon><Palette size={16} /></SectionIcon>
          <SectionTitle>Thèmes</SectionTitle>
          <div style={{ marginLeft: 'auto' }}>
            <button onClick={() => setBuilderOpen(!builderOpen)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'var(--gradient)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <Plus size={14} /> Créer un thème
            </button>
          </div>
        </SectionHeader>
        <ThemeGrid>
          {Object.entries(themes).map(([key, t]) => (
            <ThemeCard key={key} $active={currentTheme === key} $themeColor={t.primary} onClick={() => setCurrentTheme(key)}>
              {currentTheme === key && <ThemeActiveCheck $color={t.primary}><Check size={10} /></ThemeActiveCheck>}
              {t.isCustom && (
                <div style={{ position: 'absolute', top: 4, left: 4, zIndex: 2 }} onClick={(e) => { e.stopPropagation(); removeCustomTheme(key); }}>
                  <Trash2 size={12} style={{ color: 'var(--error)', cursor: 'pointer', background: 'rgba(0,0,0,0.5)', borderRadius: 4, padding: 2 }} />
                </div>
              )}
              <ThemePreview $bg={t.background}>
                <ThemePreviewSidebar $color={t.sidebarBg || t.surface}>
                  <ThemePreviewDot $color={t.primary} /><ThemePreviewDot $color={t.textMuted} /><ThemePreviewDot $color={t.textMuted} />
                </ThemePreviewSidebar>
                <ThemePreviewContent>
                  <ThemePreviewBar $color={t.primary} $width="60%" />
                  <ThemePreviewCard $color={t.cardBg || t.surface} $border={t.cardBorder || t.border} />
                  <ThemePreviewBar $color={t.accent || t.secondary} $width="40%" />
                </ThemePreviewContent>
              </ThemePreview>
              <ThemeInfo>
                <ThemeName>{t.name}</ThemeName>
                <ThemeMode>{t.isDark ? <Moon size={10} /> : <Sun size={10} />}{t.isDark ? 'Dark' : 'Light'}</ThemeMode>
              </ThemeInfo>
            </ThemeCard>
          ))}
        </ThemeGrid>
      </Section>

      {/* Theme Builder (Coolors-style) */}
      {builderOpen && (
        <Section>
          <SectionHeader>
            <SectionIcon><Plus size={16} /></SectionIcon>
            <SectionTitle>Theme Builder</SectionTitle>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button onClick={generateHarmoniousPalette} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'var(--gradient)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                <RefreshCw size={12} /> Generate
              </button>
              <button onClick={() => setBuilderOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>
                <X size={14} />
              </button>
            </div>
          </SectionHeader>

          {/* Color palette strip with hex input + lock + color picker */}
          <div style={{ display: 'flex', borderRadius: 12, overflow: 'hidden', marginBottom: 12, border: '1px solid var(--border)' }}>
            {['primary', 'secondary', 'accent', 'background', 'surface'].map((colorKey) => (
              <div key={colorKey} style={{ flex: 1, background: builderColors[colorKey], display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '12px 4px', gap: 6, position: 'relative', minHeight: 100 }}>
                <span style={{ fontSize: 8, fontWeight: 800, textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)', textShadow: '0 1px 4px rgba(0,0,0,0.6)', letterSpacing: 1 }}>{colorKey}</span>
                <input
                  type="text"
                  value={builderColors[colorKey].toUpperCase()}
                  onChange={(e) => handleHexInput(colorKey, e.target.value)}
                  style={{ width: '90%', maxWidth: 80, padding: '3px 4px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(0,0,0,0.35)', color: '#fff', fontSize: 10, fontWeight: 700, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                />
                <div style={{ display: 'flex', gap: 4 }}>
                  <label style={{ cursor: 'pointer', background: 'rgba(0,0,0,0.3)', borderRadius: 4, padding: '2px 4px', display: 'flex', alignItems: 'center' }} title="Color picker">
                    <Palette size={10} color="rgba(255,255,255,0.8)" />
                    <input type="color" value={builderColors[colorKey]} onChange={(e) => setBuilderColors(prev => ({ ...prev, [colorKey]: e.target.value }))} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                  </label>
                  <button onClick={() => toggleLock(colorKey)} style={{ background: 'rgba(0,0,0,0.3)', border: 'none', borderRadius: 4, padding: '2px 4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }} title={lockedColors[colorKey] ? 'Unlock' : 'Lock'}>
                    {lockedColors[colorKey] ? <Lock size={10} color="rgba(255,255,255,0.9)" /> : <Unlock size={10} color="rgba(255,255,255,0.5)" />}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Rich visualizer — geometric shapes + mini dashboard inspired by design studios */}
          {builderPreview && (
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', marginBottom: 16 }}>
              {/* Geometric art strip */}
              <div style={{ background: builderPreview.background, padding: 16, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
                <svg width="180" height="80" viewBox="0 0 180 80">
                  <circle cx="25" cy="40" r="22" fill={builderPreview.primary} opacity="0.9" />
                  <circle cx="45" cy="28" r="14" fill={builderPreview.secondary} opacity="0.7" />
                  <rect x="58" y="10" width="35" height="35" rx="4" fill={builderPreview.accent} opacity="0.85" />
                  <rect x="68" y="30" width="25" height="40" rx="3" fill={builderPreview.primary} opacity="0.5" />
                  <polygon points="105,5 130,50 80,50" fill={builderPreview.secondary} opacity="0.6" />
                  <circle cx="140" cy="45" r="30" fill={builderPreview.accent} opacity="0.3" />
                  <circle cx="155" cy="25" r="18" fill={builderPreview.primary} opacity="0.7" />
                  <rect x="148" y="50" width="28" height="20" rx="4" fill={builderPreview.secondary} opacity="0.8" />
                </svg>
              </div>
              {/* Mini dashboard mockup */}
              <div style={{ background: builderPreview.background, padding: '0 16px 16px', display: 'flex', gap: 10 }}>
                {/* Sidebar */}
                <div style={{ width: 36, background: builderPreview.surface, borderRadius: 8, padding: '10px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: builderPreview.primary }} />
                  <div style={{ width: 8, height: 2, borderRadius: 1, background: builderPreview.textMuted, opacity: 0.5 }} />
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: builderPreview.secondary, opacity: 0.6 }} />
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: builderPreview.textMuted, opacity: 0.3 }} />
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: builderPreview.textMuted, opacity: 0.3 }} />
                </div>
                {/* Main content */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Stats row */}
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[builderPreview.primary, builderPreview.secondary, builderPreview.accent].map((c, i) => (
                      <div key={i} style={{ flex: 1, background: builderPreview.cardBg, border: `1px solid ${builderPreview.cardBorder}`, borderRadius: 6, padding: '8px 6px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ width: '50%', height: 4, borderRadius: 2, background: builderPreview.textMuted, opacity: 0.4 }} />
                        <div style={{ fontSize: 12, fontWeight: 800, color: c, fontFamily: 'monospace' }}>{['$12.4k', '16,601', '33%'][i]}</div>
                      </div>
                    ))}
                  </div>
                  {/* Chart area */}
                  <div style={{ background: builderPreview.cardBg, border: `1px solid ${builderPreview.cardBorder}`, borderRadius: 6, padding: 8, display: 'flex', alignItems: 'flex-end', gap: 3, height: 40 }}>
                    {[0.4, 0.7, 0.5, 0.9, 0.6, 0.8, 0.3, 0.75, 0.55, 0.85].map((h, i) => (
                      <div key={i} style={{ flex: 1, height: `${h * 100}%`, borderRadius: 2, background: i % 3 === 0 ? builderPreview.primary : i % 3 === 1 ? builderPreview.secondary : builderPreview.accent, opacity: 0.8 }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Name + Save */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input value={builderName} onChange={(e) => setBuilderName(e.target.value)} placeholder="Nom du thème..." style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 }} />
            <button onClick={saveCustomTheme} disabled={!builderName.trim()} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: builderName.trim() ? 'var(--gradient)' : 'var(--surface)', border: 'none', borderRadius: 8, color: builderName.trim() ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 700, cursor: builderName.trim() ? 'pointer' : 'not-allowed' }}>
              <Save size={14} /> Enregistrer
            </button>
          </div>
        </Section>
      )}

      {/* ── Sidebar customization (merged from Sidebars panel) ── */}
      <Section>
        <SectionHeader>
          <SectionIcon style={{ background: 'linear-gradient(135deg, #a591ef, #6366f1)' }}><PanelLeftClose size={16} /></SectionIcon>
          <SectionTitle>Sidebar icônes (barre étroite)</SectionTitle>
        </SectionHeader>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Icônes de navigation en bas de la barre étroite à gauche. Le menu Paramètres est toujours visible.
        </div>
        <ToggleGrid>
          {ICON_SIDEBAR_ITEMS.map(item =>
            renderToggleRow(item, item.locked ? true : sidebarConfig.iconSidebar[item.key] !== false, toggleIconSidebar, item.locked)
          )}
        </ToggleGrid>
      </Section>

      <Section>
        <SectionHeader>
          <SectionIcon style={{ background: 'linear-gradient(135deg, #3b82f6, #06b6d4)' }}><PanelLeft size={16} /></SectionIcon>
          <SectionTitle>Sidebar menus (panneau étendu)</SectionTitle>
        </SectionHeader>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Sections de navigation dans le panneau étendu (deuxième sidebar). Permet de masquer les menus que vous n'utilisez pas.
        </div>
        <ToggleGrid>
          {TEXT_SECTION_ITEMS.map(item =>
            renderToggleRow(item, sidebarConfig.textSidebar.sections[item.key] !== false, toggleTextSection)
          )}
        </ToggleGrid>
      </Section>

      <Section>
        <SectionHeader>
          <SectionIcon style={{ background: 'linear-gradient(135deg, #f59e0b, #ef4444)' }}><LayoutGrid size={16} /></SectionIcon>
          <SectionTitle>Navigation rapide (bas du panneau)</SectionTitle>
        </SectionHeader>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Liens de navigation fixés en bas du panneau étendu pour un accès rapide aux pages principales.
        </div>
        <ToggleGrid>
          {BOTTOM_NAV_ITEMS.map(item =>
            renderToggleRow(item, sidebarConfig.textSidebar.bottomNav[item.key] !== false, toggleBottomNav)
          )}
        </ToggleGrid>
      </Section>
    </>
  );
  };

  const renderLicence = () => (
    <>
      <BackButton onClick={() => setActivePanel(null)}><ArrowLeft size={16} /> Retour</BackButton>
      <PanelTitle><Key size={22} color="#22c55e" /> Licence</PanelTitle>
      <LicencePlanCard>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Shield size={20} /><h3>Plan {licenceInfo.plan}</h3></div>
        <p>Jusqu'à {licenceInfo.maxDevices} appareils connectés</p>
      </LicencePlanCard>
      <Section>
        <SectionHeader><SectionIcon><CheckCircle size={16} /></SectionIcon><SectionTitle>Fonctionnalités incluses</SectionTitle></SectionHeader>
        <LicenceFeatureGrid>
          {['Interactions', 'Scraping', 'Unfollow', 'DM Auto', 'Warmup', 'Filtrage avancé', 'Scheduling', 'Enrichissement Gemini'].map((feat, i) => (
            <LicenceFeature key={feat} $active={i < licenceInfo.features.length}>
              <CheckCircle size={14} /> {feat}
            </LicenceFeature>
          ))}
        </LicenceFeatureGrid>
      </Section>
    </>
  );

  const renderApiKeyCard = ({ id, name, icon: Icon, color, placeholder, desc }) => {
    const isSet = apiKeys[`${id}_set`];
    const maskedKey = apiKeys[id] || '';
    const saving = apiKeySaving[id];
    const status = apiKeyStatus[id];
    return (
      <div key={id} style={{ background: 'var(--card-bg)', border: `1px solid ${isSet ? 'var(--success)' : 'var(--border)'}`, borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', overflow: 'hidden' }}>
        {isSet && <div style={{ position: 'absolute', top: 0, right: 0, width: 40, height: 40, background: 'var(--success)', opacity: 0.08, borderRadius: '0 0 0 40px' }} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon size={16} color={color} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              {name}
              {isSet && <CheckCircle size={12} color="var(--success)" />}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{desc}</div>
          </div>
        </div>
        {isSet && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)' }}>
            <Lock size={10} color="var(--success)" />
            <code style={{ fontSize: 10, color: 'var(--text-secondary)', flex: 1, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{maskedKey}</code>
            <button onClick={() => deleteApiKey(id)} style={{ background: 'none', border: 'none', color: 'var(--error)', cursor: 'pointer', padding: 2, display: 'flex' }}>
              <Trash2 size={11} />
            </button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="password" placeholder={isSet ? 'Remplacer...' : placeholder} value={apiKeyInputs[id] || ''} onChange={(e) => setApiKeyInputs(p => ({ ...p, [id]: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && saveApiKey(id)} style={{ flex: 1, padding: '8px 10px', background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 6, fontSize: 12, color: 'var(--text-primary)', fontFamily: 'monospace', minWidth: 0 }} />
          <button onClick={() => saveApiKey(id)} disabled={!apiKeyInputs[id]?.trim() || saving} style={{ padding: '8px 12px', background: apiKeyInputs[id]?.trim() ? color : 'var(--surface)', border: 'none', borderRadius: 6, color: apiKeyInputs[id]?.trim() ? '#fff' : 'var(--text-muted)', fontSize: 11, fontWeight: 700, cursor: apiKeyInputs[id]?.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
            {saving ? '...' : <><Save size={11} /></>}
          </button>
        </div>
        {status === 'saved' && <div style={{ fontSize: 10, color: 'var(--success)', fontWeight: 600 }}>Sauvegardé</div>}
        {status === 'error' && <div style={{ fontSize: 10, color: 'var(--error)', fontWeight: 600 }}>Erreur</div>}
      </div>
    );
  };

  const renderAI = () => (
    <>
      <BackButton onClick={() => setActivePanel(null)}><ArrowLeft size={16} /> Retour</BackButton>
      <PanelTitle><Brain size={22} color="#8b5cf6" /> Intelligence Artificielle & API</PanelTitle>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, padding: '8px 12px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
        Clés stockées sur le serveur (<code style={{ background: 'var(--card-bg)', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>data/credentials.json</code>) et chargées automatiquement par les services.
      </div>

      {/* API Keys — 2-column grid */}
      <Section>
        <SectionHeader><SectionIcon><Key size={16} /></SectionIcon><SectionTitle>Clés API</SectionTitle></SectionHeader>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {API_SERVICES.map(s => renderApiKeyCard(s))}
        </div>
      </Section>

      {/* Per-service AI Model Configuration */}
      <Section>
        <SectionHeader><SectionIcon><Sparkles size={16} /></SectionIcon><SectionTitle>Modèles IA par service</SectionTitle></SectionHeader>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
          Chaque service utilise son propre modèle, configurable indépendamment. Les modèles sont partagés entre tous les postes via <code style={{ background: 'var(--card-bg)', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>app_settings</code>.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {aiServices.map(svc => {
            const providerColors = { gemini: '#ea580c', openai: '#10a37f', claude: '#d97706', multi: '#8b5cf6' };
            const providerLabels = { gemini: 'Google Gemini', openai: 'OpenAI', claude: 'Anthropic', multi: 'Multi-provider' };
            const color = providerColors[svc.provider] || '#6b7280';
            const isEditable = svc.editable !== false;
            const selected = aiServiceSelections[svc.id] || svc.currentModel;
            const changed = isEditable && selected !== svc.currentModel;
            const saving = aiServiceSaving[svc.id];
            const status = aiServiceStatus[svc.id];

            return (
              <div key={svc.id} style={{ background: 'var(--card-bg)', border: `1px solid ${changed ? '#f59e0b' : 'var(--border)'}`, borderRadius: 10, padding: 14, transition: 'border-color 0.2s' }}>
                {/* Service header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Sparkles size={16} color={color} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{svc.name}</span>
                      <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: `${color}18`, color, fontWeight: 700, textTransform: 'uppercase' }}>{providerLabels[svc.provider] || svc.provider}</span>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.3 }}>{svc.desc}</div>
                  </div>
                </div>

                {/* Model selector or info */}
                {isEditable && svc.models?.length > 0 ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select
                      value={selected}
                      onChange={e => setAiServiceSelections(p => ({ ...p, [svc.id]: e.target.value }))}
                      style={{
                        flex: 1, padding: '8px 10px', background: 'var(--input-bg)', border: '1px solid var(--input-border)',
                        borderRadius: 6, fontSize: 12, color: 'var(--text-primary)', cursor: 'pointer', appearance: 'auto', fontWeight: 600,
                      }}
                    >
                      {svc.models.map(m => (
                        <option key={m.id} value={m.id}>
                          {m.label} — {m.desc} (${m.input} / ${m.output} per 1M tokens)
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => saveAiServiceModel(svc.id)}
                      disabled={!changed || saving}
                      style={{
                        padding: '8px 14px', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700,
                        cursor: changed && !saving ? 'pointer' : 'not-allowed',
                        background: changed ? color : 'var(--surface)',
                        color: changed ? '#fff' : 'var(--text-muted)',
                        display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                      }}
                    >
                      {saving ? '...' : <><Save size={11} /> OK</>}
                    </button>
                  </div>
                ) : (
                  <div style={{ padding: '8px 10px', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)', fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>
                    {svc.currentModel} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>— sélectionné par l'utilisateur dans le panel chat</span>
                  </div>
                )}

                {/* Status messages */}
                {status === 'saved' && <div style={{ fontSize: 10, color: 'var(--success)', fontWeight: 600, marginTop: 4 }}>✓ Modèle sauvegardé</div>}
                {status === 'error' && <div style={{ fontSize: 10, color: 'var(--error)', fontWeight: 600, marginTop: 4 }}>✗ Erreur de sauvegarde</div>}
              </div>
            );
          })}
          {aiServices.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>Chargement des services...</div>
          )}
        </div>
      </Section>

      {/* Google OAuth — Client ID + Client Secret */}
      <Section>
        <SectionHeader><SectionIcon><Globe size={16} /></SectionIcon><SectionTitle>Google OAuth (Gmail, Calendar, Sheets)</SectionTitle></SectionHeader>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
          Client ID et Client Secret pour l'authentification OAuth 2.0 des services Google (Gmail API, Calendar API, Sheets API).
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {GOOGLE_AUTH_FIELDS.map(({ id, label, placeholder, icon: Icon }) => {
            const isSet = apiKeys[`${id}_set`];
            const maskedKey = apiKeys[id] || '';
            const saving = apiKeySaving[id];
            const status = apiKeyStatus[id];
            return (
              <div key={id} style={{ background: 'var(--card-bg)', border: `1px solid ${isSet ? 'var(--success)' : 'var(--border)'}`, borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: '#4285f418', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={14} color="#4285f4" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {label}
                      {isSet && <CheckCircle size={11} color="var(--success)" />}
                    </div>
                  </div>
                </div>
                {isSet && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)' }}>
                    <Lock size={10} color="var(--success)" />
                    <code style={{ fontSize: 10, color: 'var(--text-secondary)', flex: 1, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{maskedKey}</code>
                    <button onClick={() => deleteApiKey(id)} style={{ background: 'none', border: 'none', color: 'var(--error)', cursor: 'pointer', padding: 2, display: 'flex' }}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="password" placeholder={isSet ? 'Remplacer...' : placeholder} value={apiKeyInputs[id] || ''} onChange={(e) => setApiKeyInputs(p => ({ ...p, [id]: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && saveApiKey(id)} style={{ flex: 1, padding: '8px 10px', background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 6, fontSize: 11, color: 'var(--text-primary)', fontFamily: 'monospace', minWidth: 0 }} />
                  <button onClick={() => saveApiKey(id)} disabled={!apiKeyInputs[id]?.trim() || saving} style={{ padding: '8px 12px', background: apiKeyInputs[id]?.trim() ? '#4285f4' : 'var(--surface)', border: 'none', borderRadius: 6, color: apiKeyInputs[id]?.trim() ? '#fff' : 'var(--text-muted)', fontSize: 11, fontWeight: 700, cursor: apiKeyInputs[id]?.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center' }}>
                    {saving ? '...' : <Save size={11} />}
                  </button>
                </div>
                {status === 'saved' && <div style={{ fontSize: 10, color: 'var(--success)', fontWeight: 600 }}>Sauvegardé</div>}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Help links */}
      <Section>
        <SectionHeader><SectionIcon><Info size={16} /></SectionIcon><SectionTitle>Comment obtenir les clés</SectionTitle></SectionHeader>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {[
            ['Google Gemini', 'https://aistudio.google.com/apikey', 'AI Studio > Create API Key'],
            ['OpenAI', 'https://platform.openai.com/api-keys', 'Settings > API Keys'],
            ['Claude', 'https://console.anthropic.com/settings/keys', 'Console > API Keys'],
            ['Google OAuth', 'https://console.cloud.google.com/apis/credentials', 'GCP > APIs > Credentials > OAuth 2.0'],
            ['Notion', 'https://www.notion.so/my-integrations', 'My Integrations > New'],
          ].map(([name, url, hint]) => (
            <a key={name} href={url} target="_blank" rel="noopener noreferrer" style={{ padding: '8px 12px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{name}</span>
              <span style={{ fontSize: 10, color: 'var(--primary)', fontWeight: 600 }}>{hint}</span>
            </a>
          ))}
        </div>
      </Section>

      {/* ── AI Usage Dashboard ── */}
      <Section>
        <SectionHeader>
          <SectionIcon style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)' }}><BarChart3 size={16} /></SectionIcon>
          <SectionTitle>Utilisation des API IA</SectionTitle>
        </SectionHeader>

        {/* Period selector */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[
            { key: 'today', label: "Aujourd'hui" },
            { key: 'week', label: '7 jours' },
            { key: 'month', label: '30 jours' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { setAiUsagePeriod(key); loadAiUsage(key); }}
              style={{
                padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)',
                background: aiUsagePeriod === key ? '#8b5cf6' : 'var(--surface)',
                color: aiUsagePeriod === key ? '#fff' : 'var(--text-secondary)',
                fontSize: 11, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => loadAiUsage(aiUsagePeriod)}
            style={{ marginLeft: 'auto', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <RefreshCw size={12} color="var(--text-muted)" />
          </button>
        </div>

        {aiUsageLoading && <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>Chargement...</div>}

        {aiUsage && !aiUsageLoading && (
          <>
            {/* Totals cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Appels API', value: Number(aiUsage.totals?.calls || 0).toLocaleString(), icon: Activity, color: '#8b5cf6' },
                { label: 'Tokens Input', value: Number(aiUsage.totals?.input_tokens || 0).toLocaleString(), icon: Zap, color: '#22c55e' },
                { label: 'Tokens Output', value: Number(aiUsage.totals?.output_tokens || 0).toLocaleString(), icon: Zap, color: '#f59e0b' },
                { label: 'Coût estimé', value: `$${Number(aiUsage.totals?.cost_usd || 0).toFixed(4)}`, icon: DollarSign, color: '#ef4444' },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                  <Icon size={18} color={color} style={{ marginBottom: 6 }} />
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{value}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Per-model breakdown with progress bars */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>Utilisation par modèle</div>
            {aiUsage.byModel && aiUsage.byModel.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(() => {
                  const maxTokens = Math.max(...aiUsage.byModel.map(m => Number(m.total_tokens || 0)), 1);
                  const providerColors = { gemini: '#ea580c', openai: '#10b981', claude: '#8b5cf6' };
                  return aiUsage.byModel.map(m => {
                    const total = Number(m.total_tokens || 0);
                    const pct = (total / maxTokens) * 100;
                    const color = providerColors[m.provider] || '#6b7280';
                    return (
                      <div key={m.model} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{m.model}</span>
                            <span style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--surface)', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>{m.provider}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 12, fontSize: 10, color: 'var(--text-muted)' }}>
                            <span>{Number(m.calls).toLocaleString()} appels</span>
                            <span style={{ fontWeight: 700, color }}>${Number(m.cost_usd || 0).toFixed(4)}</span>
                          </div>
                        </div>
                        {/* Progress bar */}
                        <div style={{ height: 6, background: 'var(--surface)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${color}, ${color}99)`, borderRadius: 3, transition: 'width 0.5s ease' }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10, color: 'var(--text-muted)' }}>
                          <span>In: {Number(m.input_tokens || 0).toLocaleString()} tokens</span>
                          <span>Out: {Number(m.output_tokens || 0).toLocaleString()} tokens</span>
                          <span style={{ fontWeight: 700 }}>Total: {total.toLocaleString()} tokens</span>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 20, background: 'var(--surface)', borderRadius: 10 }}>
                Aucune utilisation enregistrée pour cette période. Les appels via le chat IA, l'enrichissement Gemini et l'analyse de profils seront automatiquement trackés.
              </div>
            )}

            {/* Daily breakdown mini-chart */}
            {aiUsage.daily && aiUsage.daily.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>Activité quotidienne</div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 60, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 10px 8px' }}>
                  {(() => {
                    // Group by day
                    const dayMap = {};
                    aiUsage.daily.forEach(d => {
                      const day = d.day?.split('T')[0] || d.day;
                      if (!dayMap[day]) dayMap[day] = { tokens: 0, cost: 0 };
                      dayMap[day].tokens += Number(d.total_tokens || 0);
                      dayMap[day].cost += Number(d.cost_usd || 0);
                    });
                    const days = Object.entries(dayMap);
                    const maxT = Math.max(...days.map(([, v]) => v.tokens), 1);
                    const providerColors = { gemini: '#ea580c', openai: '#10b981', claude: '#8b5cf6' };
                    return days.map(([day, v]) => {
                      const h = Math.max((v.tokens / maxT) * 40, 2);
                      const dayLabel = new Date(day).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });
                      // Find dominant provider for this day
                      const providerForDay = aiUsage.daily.find(d => (d.day?.split('T')[0] || d.day) === day)?.provider || 'gemini';
                      return (
                        <div key={day} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }} title={`${dayLabel}: ${v.tokens.toLocaleString()} tokens — $${v.cost.toFixed(4)}`}>
                          <div style={{ width: '100%', maxWidth: 24, height: h, background: `linear-gradient(180deg, ${providerColors[providerForDay] || '#8b5cf6'}, ${providerColors[providerForDay] || '#8b5cf6'}66)`, borderRadius: 3 }} />
                          <span style={{ fontSize: 8, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{dayLabel}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Provider summary */}
            {aiUsage.byProvider && aiUsage.byProvider.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>Coût par fournisseur</div>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(aiUsage.byProvider.length, 3)}, 1fr)`, gap: 10 }}>
                  {(() => {
                    const providerLabels = { gemini: 'Google Gemini', openai: 'OpenAI', claude: 'Anthropic Claude' };
                    const providerColors = { gemini: '#ea580c', openai: '#10b981', claude: '#8b5cf6' };
                    const totalCost = aiUsage.byProvider.reduce((s, p) => s + Number(p.cost_usd || 0), 0) || 1;
                    return aiUsage.byProvider.map(p => {
                      const costPct = (Number(p.cost_usd || 0) / totalCost) * 100;
                      const color = providerColors[p.provider] || '#6b7280';
                      return (
                        <div key={p.provider} style={{ background: 'var(--card-bg)', border: `1px solid ${color}33`, borderRadius: 10, padding: 14 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{providerLabels[p.provider] || p.provider}</span>
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 800, color }}>${Number(p.cost_usd || 0).toFixed(4)}</div>
                          <div style={{ height: 4, background: 'var(--surface)', borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${costPct}%`, background: color, borderRadius: 2 }} />
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}>{Number(p.calls).toLocaleString()} appels — {Number(p.total_tokens || 0).toLocaleString()} tokens</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </>
        )}
      </Section>
    </>
  );

  const renderNotifications = () => (
    <>
      <BackButton onClick={() => setActivePanel(null)}><ArrowLeft size={16} /> Retour</BackButton>
      <PanelTitle><Bell size={22} color="#f59e0b" /> Notifications</PanelTitle>
      <Section>
        <SectionHeader><SectionIcon><Bell size={16} /></SectionIcon><SectionTitle>Alertes</SectionTitle></SectionHeader>
        <ToggleGrid>
          <ToggleItem $enabled={true}><ToggleLabelContainer><ToggleIconWrapper $enabled={true}><Bell size={18} /></ToggleIconWrapper><ToggleLabel $enabled={true}>Alertes de session</ToggleLabel></ToggleLabelContainer><ToggleSwitch $enabled={true} /></ToggleItem>
          <ToggleItem $enabled={true}><ToggleLabelContainer><ToggleIconWrapper $enabled={true}><XCircle size={18} /></ToggleIconWrapper><ToggleLabel $enabled={true}>Erreurs critiques</ToggleLabel></ToggleLabelContainer><ToggleSwitch $enabled={true} /></ToggleItem>
        </ToggleGrid>
      </Section>
    </>
  );

  const renderDatabase = () => (
    <>
      <BackButton onClick={() => setActivePanel(null)}><ArrowLeft size={16} /> Retour</BackButton>
      <PanelTitle><Database size={22} color="#06b6d4" /> Base de données</PanelTitle>
      <Section>
        <SectionHeader><SectionIcon><HardDrive size={16} /></SectionIcon><SectionTitle>Mode base de données</SectionTitle></SectionHeader>
        <div style={{ padding: '8px 0 4px', marginBottom: 8 }}>
          <small style={{ color: 'var(--text-muted)', fontSize: 12 }}>
            Choisissez comment l'application accède aux données. En production (utilisateurs finaux), utilisez <b>SQLite uniquement</b>.
          </small>
        </div>
        {[
          { key: 'sqlite',   label: 'SQLite uniquement',   icon: HardDrive, color: 'var(--primary)',  desc: 'Base locale — aucun serveur requis (mode production)' },
          { key: 'both',     label: 'Les deux (sync)',      icon: Database,  color: '#10b981',         desc: 'PostgreSQL en priorité + synchronisation SQLite (mode dev)' },
          { key: 'postgres', label: 'PostgreSQL uniquement',icon: Server,    color: '#06b6d4',         desc: 'Accès direct au serveur distant (mode admin complet)' },
        ].map(({ key, label, icon: Icon, color, desc }) => (
          <DbToggleRow
            key={key}
            onClick={() => !dbModeSaving && changeDbMode(key)}
            style={{ cursor: dbModeSaving ? 'wait' : 'pointer', opacity: dbModeSaving ? 0.7 : 1, border: dbMode === key ? `1.5px solid ${color}` : undefined }}
          >
            <DbToggleLabel>
              <Icon size={20} color={color} />
              <div><span>{label}</span><small>{desc}</small></div>
            </DbToggleLabel>
            <DbStatusBadge $active={dbMode === key}>
              {dbMode === key ? 'Actif' : 'Inactif'}
            </DbStatusBadge>
          </DbToggleRow>
        ))}
        {dbModeChanged && (
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px' }}>
            <span style={{ fontSize: '13px', color: '#10b981', flex: 1 }}>Mode changé — rechargez pour appliquer la configuration.</span>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '6px 14px', fontSize: '13px', fontWeight: 600, background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            >
              Recharger
            </button>
          </div>
        )}
      </Section>
      <Section>
        <SectionHeader><SectionIcon><Wifi size={16} /></SectionIcon><SectionTitle>Streaming & Remote</SectionTitle></SectionHeader>
        <ToggleGrid>
          <ToggleItem $enabled={screenStreamingEnabled} onClick={handleScreenStreamingToggle}>
            <ToggleLabelContainer><ToggleIconWrapper $enabled={screenStreamingEnabled}><Smartphone size={18} /></ToggleIconWrapper><ToggleLabel $enabled={screenStreamingEnabled}>Screen Streaming</ToggleLabel></ToggleLabelContainer>
            <ToggleSwitch $enabled={screenStreamingEnabled} onClick={(e) => { e.stopPropagation(); handleScreenStreamingToggle(); }} />
          </ToggleItem>
        </ToggleGrid>
        <RemoteSection>
          <RemoteConfig>
            <RemoteInput>
              <label>IP du PC Fixe (contrôle à distance)</label>
              <input type="text" placeholder="192.168.1.100" value={remoteHost} onChange={(e) => setRemoteHost(e.target.value)} disabled={remoteConnected} />
            </RemoteInput>
            <RemoteButton $connected={remoteConnected} onClick={handleRemoteToggle} disabled={!remoteHost && !remoteConnected}>
              {remoteConnected ? <><WifiOff size={18} /> Déconnecter</> : <><Wifi size={18} /> Connecter</>}
            </RemoteButton>
          </RemoteConfig>
        </RemoteSection>
      </Section>
    </>
  );

  const renderAbout = () => (
    <>
      <BackButton onClick={() => setActivePanel(null)}><ArrowLeft size={16} /> Retour</BackButton>
      <PanelTitle><Info size={22} color="var(--text-muted)" /> À propos</PanelTitle>
      <Section>
        <SectionHeader><SectionIcon><Info size={16} /></SectionIcon><SectionTitle>Application</SectionTitle></SectionHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[['Application', 'QLICKR Manager'], ['Version', '2.0.0'], ['Framework', 'React + Express + SQLite'], ['Auteur', 'QLICKR'], ['Licence', 'Propriétaire']].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>{k}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{v}</span>
            </div>
          ))}
        </div>
      </Section>
    </>
  );

  // ── Main card navigation ──────────────────────────────────────────
  const settingsCategories = [
    { id: 'apparence', icon: Palette, color: '#a591ef', title: 'Apparence', desc: 'Thème, couleurs, sidebars et affichage' },
    { id: 'licence', icon: Key, color: '#22c55e', title: 'Licence', desc: 'Plan, appareils et fonctionnalités' },
    { id: 'ai', icon: Brain, color: '#8b5cf6', title: 'Intelligence Artificielle', desc: 'Clés API, usage et coûts par modèle' },
    { id: 'notifications', icon: Bell, color: '#f59e0b', title: 'Notifications', desc: 'Alertes de session et erreurs' },
    { id: 'database', icon: Database, color: '#06b6d4', title: 'Base de données', desc: 'SQLite, PostgreSQL et connexions' },
    { id: 'about', icon: Info, color: '#6b7280', title: 'À propos', desc: "Version et informations" },
  ];

  const panelRenderers = {
    apparence: renderAppearance,
    licence: renderLicence,
    ai: renderAI,
    notifications: renderNotifications,
    database: renderDatabase,
    about: renderAbout,
  };

  return (
    <PageContainer>
      <SharedPageHeader icon={SettingsIcon} title="Paramètres" subtitle="Configurez l'application" />

      {activePanel === null ? (
        <NavGrid>
          {settingsCategories.map(({ id, icon: Icon, color, title, desc }) => (
            <NavCard key={id} $color={color} onClick={() => setActivePanel(id)}>
              <NavCardIcon $color={color}><Icon size={22} /></NavCardIcon>
              <NavCardText><h3>{title}</h3><p>{desc}</p></NavCardText>
              <NavCardArrow size={18} />
            </NavCard>
          ))}
        </NavGrid>
      ) : (
        panelRenderers[activePanel]?.() || null
      )}
    </PageContainer>
  );
};

export default Settings;
